//
//  settingsLookups.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module defines the guides of the settings lookup pages.
//

import { Permission } from "src/api/Tmd.dtos";

import { lookupGuides } from "./lookupGuides";
import { column, field } from "./pageGuide";

/** Builds the list, edit and create routes of a lookup from its list route and ID parameter. */
const routes = (list: string, idParameter: string) => ({
    list,
    item: `${list}/:${idParameter}`,
    create: `${list}/create`
});

const settingsLookupGuides = {
    ...lookupGuides(routes("/settings/general/countries", "countryId"), {
        plural: "countries",
        singular: "country",
        purpose: "Countries complete postal addresses and decide which states are offered.",
        usedIn: "the Country fields of institution, group, department and personnel addresses",
        example: "Australia",
        createPermission: Permission.CountriesCreate,
        updatePermission: Permission.CountriesUpdate
    }),
    ...lookupGuides(routes("/settings/general/states", "stateId"), {
        plural: "states",
        singular: "state",
        purpose:
            "States and territories complete postal addresses; an address can use State Other when its state is not listed.",
        usedIn: "the State fields of institution, group, department and personnel addresses",
        example: "New South Wales",
        createPermission: Permission.StatesCreate,
        updatePermission: Permission.StatesUpdate
    }),
    ...lookupGuides(routes("/settings/general/itemcomponenttypes", "itemComponentTypeId"), {
        plural: "item component types",
        singular: "item component type",
        purpose:
            "A component type classifies the reusable components that item and QA form templates are built from.",
        usedIn: "item components and QA form components",
        createPermission: Permission.ItemComponentTypesCreate,
        updatePermission: Permission.ItemComponentTypesUpdate
    }),
    ...lookupGuides(routes("/settings/general/notetypes", "noteTypeId"), {
        plural: "note types",
        singular: "note type",
        purpose:
            "Note types classify notes so they can be filtered and reported, such as correspondence or meeting minutes.",
        usedIn: "the Type field of trial, institution, group and personnel notes",
        example: "Correspondence",
        createPermission: Permission.NoteTypesCreate,
        updatePermission: Permission.NoteTypesUpdate
    }),
    ...lookupGuides(routes("/settings/general/genders", "genderId"), {
        plural: "genders",
        singular: "gender",
        purpose: "Genders are recorded against personnel.",
        usedIn: "the Gender field of personnel details",
        createPermission: Permission.GendersCreate,
        updatePermission: Permission.GendersUpdate
    }),
    ...lookupGuides(routes("/settings/general/interests", "interestId"), {
        plural: "interests",
        singular: "interest",
        purpose:
            "Interests record the areas a member wants to hear about or contribute to, and feed membership communications.",
        usedIn: "the Interests field of personnel details",
        example: "Prostate",
        createPermission: Permission.InterestsCreate,
        updatePermission: Permission.InterestsUpdate
    }),
    ...lookupGuides(routes("/settings/general/membershiptypes", "membershipTypeId"), {
        plural: "membership types",
        singular: "membership type",
        purpose:
            "Membership types record how a person belongs to TROG; they drive membership reporting and the HubSpot export.",
        usedIn: "the Membership Type field of personnel details",
        example: "Full Member",
        createPermission: Permission.MembershipTypesCreate,
        updatePermission: Permission.MembershipTypesUpdate
    }),
    ...lookupGuides(
        routes("/settings/general/professionaldisciplines", "professionalDisciplineId"),
        {
            plural: "professional disciplines",
            singular: "professional discipline",
            purpose:
                "Disciplines record a person's profession; people can add others in Other Disciplines.",
            usedIn: "the Professional Discipline field of personnel details",
            example: "Radiation Therapist",
            createPermission: Permission.ProfessionalDisciplinesCreate,
            updatePermission: Permission.ProfessionalDisciplinesUpdate
        }
    ),
    ...lookupGuides(routes("/settings/general/salutations", "salutationId"), {
        plural: "salutations",
        singular: "salutation",
        purpose: "Salutations prefix personnel names in correspondence and emails.",
        usedIn: "the Salutation field of personnel details",
        example: "Dr",
        createPermission: Permission.SalutationsCreate,
        updatePermission: Permission.SalutationsUpdate
    }),
    ...lookupGuides(routes("/settings/institution/sitelocations", "siteLocationId"), {
        plural: "site locations",
        singular: "site location",
        purpose:
            "Site locations describe where an institution is, for trial feasibility and reporting.",
        usedIn: "the Site Location field of institution details",
        example: "Metropolitan",
        createPermission: Permission.SiteLocationsCreate,
        updatePermission: Permission.SiteLocationsUpdate
    }),
    ...lookupGuides(
        routes("/settings/institution/facilityalliancemembers", "facilityAllianceMemberId"),
        {
            plural: "facility alliance members",
            singular: "facility alliance member",
            purpose:
                "Facility alliance members are the organisations an institution can belong to through the facility alliance.",
            usedIn: "the Facility Alliance Member field of institution details",
            createPermission: Permission.FacilityAllianceMembersCreate,
            updatePermission: Permission.FacilityAllianceMembersUpdate
        }
    ),
    ...lookupGuides(routes("/settings/institution/institutiontypes", "institutionTypeId"), {
        plural: "institution types",
        singular: "institution type",
        purpose: "Institution types classify institutions, for example as public or private.",
        usedIn: "the Institution Type field of institution details",
        example: "Public",
        createPermission: Permission.InstitutionTypesCreate,
        updatePermission: Permission.InstitutionTypesUpdate
    }),
    ...lookupGuides(routes("/settings/institution/institutioncodings", "institutionCodingId"), {
        plural: "institution codings",
        singular: "institution coding",
        purpose:
            "Codings are the external codes used to identify institutions in other systems and reports.",
        usedIn: "institution records",
        createPermission: Permission.InstitutionCodingCreate,
        updatePermission: Permission.InstitutionCodingUpdate
    }),
    ...lookupGuides(routes("/settings/trial/trialcategorys", "trialCategoryId"), {
        plural: "trial categories",
        singular: "trial category",
        purpose:
            "Categories group trials for reporting. A category can also check the format of the trial's number.",
        usedIn: "the Category field of trial details",
        createPermission: Permission.TrialCategoriesCreate,
        updatePermission: Permission.TrialCategoriesUpdate,
        extraColumns: [
            column(
                "Validation",
                "Whether trial numbers in this category are checked against a format."
            )
        ],
        extraFields: [
            field(
                "validationFormat",
                "Validation Format",
                "Optional. A regular expression the trial number must match when a trial uses this category, for example ^TROG \\d{2}\\.\\d{2}$ for numbers such as TROG 18.06. Leave empty to accept any number."
            ),
            field(
                "validationMessage",
                "Validation Message",
                "The message shown on the trial when its number does not match the format. Say what the number should look like."
            )
        ]
    }),
    ...lookupGuides(routes("/settings/trial/trialtypes", "trialTypeId"), {
        plural: "trial types",
        singular: "trial type",
        purpose: "Trial types classify trials by their design.",
        usedIn: "trial details",
        createPermission: Permission.TrialTypesCreate,
        updatePermission: Permission.TrialTypesUpdate
    }),
    ...lookupGuides(routes("/settings/trial/trialphases", "trialPhaseId"), {
        plural: "trial phases",
        singular: "trial phase",
        purpose: "Phases record a trial's clinical phase; a trial can have more than one.",
        usedIn: "the Phases field of trial details",
        example: "Phase III",
        createPermission: Permission.TrialPhasesCreate,
        updatePermission: Permission.TrialPhasesUpdate
    }),
    ...lookupGuides(routes("/settings/trial/tumourgroups", "tumourGroupId"), {
        plural: "tumour groups",
        singular: "tumour group",
        purpose:
            "Tumour groups associate a trial with the cancers it studies; a trial can have several and describe others in Tumour Group Other.",
        usedIn: "the Tumour Groups field of trial details",
        example: "Lung",
        createPermission: Permission.TumorGroupsCreate,
        updatePermission: Permission.TumorGroupsUpdate
    }),
    ...lookupGuides(routes("/settings/trial/trialPresentationTypes", "trialPresentationTypeId"), {
        plural: "presentation types",
        singular: "presentation type",
        purpose: "Presentation types classify the conference presentations produced from a trial.",
        usedIn: "trial output records",
        example: "Oral",
        createPermission: Permission.TrialPresentationTypesCreate,
        updatePermission: Permission.TrialPresentationTypesUpdate
    }),
    ...lookupGuides(routes("/settings/trial/trialPublicationTypes", "trialPublicationTypeId"), {
        plural: "publication types",
        singular: "publication type",
        purpose: "Publication types classify the papers and abstracts produced from a trial.",
        usedIn: "trial output records",
        example: "Journal Article",
        createPermission: Permission.TrialPublicationTypesCreate,
        updatePermission: Permission.TrialPublicationTypesUpdate
    }),
    ...lookupGuides(routes("/settings/trial/trialAnalysisTypes", "trialAnalysisTypeId"), {
        plural: "analysis types",
        singular: "analysis type",
        purpose: "Analysis types describe which analysis a trial publication reports.",
        usedIn: "trial output records",
        example: "Primary",
        createPermission: Permission.TrialAnalysisTypesCreate,
        updatePermission: Permission.TrialAnalysisTypesUpdate
    }),
    ...lookupGuides(routes("/settings/trial/involvements", "involvementId"), {
        plural: "involvement types",
        singular: "involvement type",
        purpose:
            "Involvement types record how TROG takes part in a trial, such as leading or collaborating.",
        usedIn: "the Involvement field of trial details",
        createPermission: Permission.InvolvementsCreate,
        updatePermission: Permission.InvolvementsUpdate
    }),
    ...lookupGuides(routes("/settings/trial/allocatedwps", "allocatedWPId"), {
        plural: "allocated work packages",
        singular: "work package",
        purpose: "Work packages are the funded work streams a trial can be allocated to.",
        usedIn: "the Allocated WPs field of trial details",
        createPermission: Permission.AllocatedWPsCreate,
        updatePermission: Permission.AllocatedWPsUpdate
    }),
    ...lookupGuides(routes("/settings/qa/reportingTagCategories", "reportingTagCategoryId"), {
        plural: "reporting tag categories",
        singular: "reporting tag category",
        purpose: "Categories group reporting tags so QA reports can be broken down by them.",
        usedIn: "the Reporting Tag Category field of reporting tags",
        createPermission: Permission.ReportingTagCategoriesCreate,
        updatePermission: Permission.ReportingTagCategoriesUpdate
    }),
    ...lookupGuides(routes("/settings/qa/reportingTags", "reportingTagId"), {
        plural: "reporting tags",
        singular: "reporting tag",
        purpose:
            "Reporting tags label QA measurements so results can be grouped in QA case reports.",
        usedIn: "QA measurements on form components",
        createPermission: Permission.ReportingTagsCreate,
        updatePermission: Permission.ReportingTagsUpdate,
        extraColumns: [column("Category", "The reporting tag category the tag belongs to.")],
        extraFields: [
            field(
                "reportingTagCategoryId",
                "Reporting Tag Category",
                "The category this tag is grouped under in reports. Only active categories are offered."
            )
        ]
    }),
    ...lookupGuides(routes("/settings/qa/taskTypes", "taskTypeId"), {
        plural: "task types",
        singular: "task type",
        purpose: "Task types classify the tasks recorded while a QA tracker progresses.",
        usedIn: "tracker tasks",
        createPermission: Permission.TaskTypesCreate,
        updatePermission: Permission.TaskTypesUpdate
    }),
    ...lookupGuides(routes("/settings/qa/trackerTypes", "trackerTypeId"), {
        plural: "tracker types",
        singular: "tracker type",
        purpose:
            "Tracker types say what a QA tracker follows, such as a plan review or a benchmarking exercise.",
        usedIn: "the Tracker Type field of trial QA trackers",
        createPermission: Permission.TrackerTypesCreate,
        updatePermission: Permission.TrackerTypesUpdate
    }),
    ...lookupGuides(routes("/settings/qa/trackerMilestones", "trackerMilestoneId"), {
        plural: "tracker milestones",
        singular: "tracker milestone",
        purpose: "Milestones are the stages a QA tracker moves through.",
        usedIn: "the Milestone field of trial QA trackers",
        createPermission: Permission.TrackerMilestonesCreate,
        updatePermission: Permission.TrackerMilestonesUpdate
    }),
    ...lookupGuides(routes("/settings/qa/formTemplateFileTypes", "formTemplateFileTypeId"), {
        plural: "form template file types",
        singular: "form template file type",
        purpose:
            "File types are the kinds of document a QA form accepts, such as a treatment plan or a DICOM export.",
        usedIn: "the file upload components of QA form templates",
        createPermission: Permission.FormTemplateFileTypesCreate,
        updatePermission: Permission.FormTemplateFileTypesUpdate,
        extraFields: [
            field(
                "formName",
                "Form Name",
                "The name shown to people uploading on a form, when it should differ from the Name used in settings."
            ),
            field(
                "defaultSourceDocumentStatusId",
                "Default Source Document Status",
                "The status new uploads of this type start in, for example awaiting review."
            )
        ]
    }),
    ...lookupGuides(routes("/settings/item/itemcategories", "categoryId"), {
        plural: "item categories",
        singular: "item category",
        purpose: "Categories group item templates and items in lists and imports.",
        usedIn: "the Category field of trial, institution, group and participant item templates",
        example: "Ethics",
        createPermission: Permission.CategoriesCreate,
        updatePermission: Permission.CategoriesUpdate
    })
};

export default settingsLookupGuides;
